import { useCallback, useMemo, useState } from 'react';
import { API, graphqlOperation } from 'aws-amplify';
import { navStore, useStore } from '@mcn-platform/models';
import { DELETE_FOREVER_PHRASE } from 'components/deletion/DeleteForeverDialog';
import type { OrganizationDeletionPreviewData } from 'components/deletion/OrganizationDeleteDialog';
import { formatGraphqlError } from 'utils/graphqlErrors';
import {
  cancelOrganizationDeletion,
  deleteOrganizationPermanently,
  getOrganizationPurgeStatus,
  hardDeleteOrganizationNow,
  previewOrganizationDeletion,
} from 'components/deletion/tenantDeletionGraphql';

export type OrgDeleteDialogState = {
  mode: 'hard';
  org: { id: string; name?: string };
} | null;

export type OrgDeleteFlowState = {
  org: { id: string; name?: string };
  loading: boolean;
  loadError?: string;
  previewData: OrganizationDeletionPreviewData | null;
} | null;

const PURGE_POLL_INTERVAL_MS = 8000;
const PURGE_POLL_MAX_ATTEMPTS = 150; // ~20 minutes

const refreshOrganizationList = async (store: ReturnType<typeof useStore>) => {
  // Reset AND refetch all cached `organization` lists so the visible list
  // (held in ItemNavigator state) reloads instead of getting stuck at `stubbed`.
  await store.reloadCachedListsForModel('organization');
};

const delay = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });

/**
 * Poll a single org's purge status while a hard delete runs in the background.
 *
 * This intentionally does NOT touch the cached lists each cycle — resetting the
 * list every few seconds makes the listing page churn in and out of its loading
 * state for the whole purge. We only refresh the list once, when the purge
 * reaches a terminal state (org record gone, or purge failed).
 */
const pollPurgeStatus = async (
  store: ReturnType<typeof useStore>,
  orgId: string,
  orgName?: string,
) => {
  for (let attempt = 0; attempt < PURGE_POLL_MAX_ATTEMPTS; attempt += 1) {
    await delay(PURGE_POLL_INTERVAL_MS);
    let org: { status?: string; purgeStatus?: string; purgeError?: string } | null;
    try {
      const result: any = await API.graphql(
        graphqlOperation(getOrganizationPurgeStatus, { id: orgId }),
      );
      org = result?.data?.getOrganization ?? null;
    } catch {
      continue;
    }
    if (!org) {
      navStore.showSnackBar(`Organization "${orgName || orgId}" permanently deleted.`, 'success');
      await refreshOrganizationList(store);
      return;
    }
    if (org.purgeStatus === 'failed') {
      navStore.showErrorDialog(
        'Hard delete failed',
        org.purgeError
          || 'The organization purge failed. Check provisioner logs and retry.',
      );
      await refreshOrganizationList(store);
      return;
    }
  }
};

export const useOrganizationDeletion = () => {
  const store = useStore();
  const isSuperUser = store.currentUserRoles?.includes('SUPERUSER') ?? false;
  const [deleteFlow, setDeleteFlow] = useState<OrgDeleteFlowState>(null);
  const [deleteDialog, setDeleteDialog] = useState<OrgDeleteDialogState>(null);

  const openDeleteFlow = useCallback(async (org: { id: string; name?: string }) => {
    setDeleteFlow({
      org,
      loading: true,
      previewData: null,
    });
    try {
      const result: any = await API.graphql(
        graphqlOperation(previewOrganizationDeletion, { organizationID: org.id }),
      );
      setDeleteFlow({
        org,
        loading: false,
        previewData: result?.data?.previewOrganizationDeletion ?? null,
      });
    } catch (err: unknown) {
      setDeleteFlow({
        org,
        loading: false,
        loadError: formatGraphqlError(err),
        previewData: null,
      });
    }
  }, []);

  const handleSoftDelete = async () => {
    if (!deleteFlow?.org) return;
    await API.graphql(graphqlOperation(deleteOrganizationPermanently, {
      organizationID: deleteFlow.org.id,
      confirmationPhrase: DELETE_FOREVER_PHRASE,
    }));
    navStore.showSnackBar('Organization scheduled for deletion in 30 days.', 'success');
    refreshOrganizationList(store);
  };

  const handleHardDelete = async () => {
    if (!deleteDialog) return;
    const { org } = deleteDialog;
    try {
      await API.graphql(graphqlOperation(hardDeleteOrganizationNow, {
        organizationID: org.id,
        confirmationPhrase: DELETE_FOREVER_PHRASE,
      }));
    } catch (err: unknown) {
      navStore.showErrorDialog('Hard delete failed to start', formatGraphqlError(err));
      return;
    }
    setDeleteDialog(null);
    navStore.showSnackBar(
      'Organization purge started. This can take a few minutes — status will update as it progresses.',
      'success',
    );
    if (store.currentOrganization?.id === org.id) {
      const orgList = store.getCachedList(
        'organization',
        {},
        { field: 'name', direction: 'asc' },
        true,
      );
      const other = orgList.items.find(
        (o: { id: string; status?: string }) => o.id !== org.id
          && (o.status || 'ACTIVE') === 'ACTIVE',
      );
      if (other) await store.setCurrentOrganization(other);
    }
    await refreshOrganizationList(store);
    // Background poll; do not await so the UI stays responsive.
    void pollPurgeStatus(store, org.id, org.name);
  };

  const handleUndelete = useCallback((org: { id: string; name?: string }) => {
    navStore.showConfirmDialog(
      'Undelete organization',
      `Restore "${org.name || org.id}" to active status and cancel the scheduled purge?`,
      async () => {
        try {
          await API.graphql(graphqlOperation(cancelOrganizationDeletion, {
            organizationID: org.id,
          }));
          navStore.showSnackBar('Organization restored.', 'success');
          refreshOrganizationList(store);
        } catch (err: unknown) {
          navStore.showErrorDialog('Undelete failed', formatGraphqlError(err));
        }
      },
      'Undelete',
      undefined,
      'Cancel',
    );
  }, [store]);

  const deletionActions = useMemo(() => {
    if (!isSuperUser) return [];
    return [
      {
        name: 'deleteOrg',
        label: 'Delete',
        action: (org: { id: string; name?: string }) => { openDeleteFlow(org); },
        hidden: (org: { status?: string }) => (org.status || 'ACTIVE') !== 'ACTIVE',
      },
      {
        name: 'hardDeleteOrg',
        label: 'Hard Delete Now',
        action: (org: { id: string; name?: string }) => {
          setDeleteDialog({ mode: 'hard', org });
        },
        hidden: (org: { status?: string; purgeStatus?: string }) => {
          if (org.status === 'DELETED') return false;
          if (org.status === 'PURGING' && org.purgeStatus === 'failed') return false;
          return true;
        },
      },
      {
        name: 'undeleteOrg',
        label: 'Undelete',
        action: (org: { id: string; name?: string }) => { handleUndelete(org); },
        hidden: (org: { status?: string }) => org.status !== 'DELETED',
      },
    ];
  }, [isSuperUser, openDeleteFlow, handleUndelete]);

  return {
    deletionActions,
    isSuperUser,
    deleteFlow,
    setDeleteFlow,
    deleteDialog,
    setDeleteDialog,
    handleSoftDelete,
    handleHardDelete,
  };
};
